import React, { useState, useEffect, useRef } from 'react';
import { Upload, Download, Wifi, WifiOff, Image as ImageIcon, Play, Pause, RotateCw, Trash2 } from 'lucide-react';
import { useParams } from 'react-router';
const SPIFFSUploader = () => {
  const { id } = useParams();
  const [isConnected, setIsConnected] = useState(false);
  const [port, setPort] = useState(null);
  const [writer, setWriter] = useState(null);
  const [reader, setReader] = useState(null);
  const [baudRate, setBaudRate] = useState('115200');
  const [chunkSize, setChunkSize] = useState(1024);
  const [bufferTimer, setBufferTimer] = useState(50);
  const [log, setLog] = useState('');
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isAutoUpload, setIsAutoUpload] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [hasInitialCleared, setHasInitialCleared] = useState(false);
  
  const responseBuffer = useRef('');
  const responseCallback = useRef(null);
  const logRef = useRef(null);

  const API_URL = `https://api.dqr-display-tms.bonrix.in/api/Share/Campaign/Json/${id}`;

  const baudRateOptions = [
    '115200', '230400', '250000', '460800', '500000', '921600', '1000000', '2000000'
  ];

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] [${type.toUpperCase()}] ${message}`;
    setLog(prev => prev + logEntry + '\n');
  };

  const clearLog = () => {
    setLog('');
  };


    // Clear all images from SPIFFS
  const clearAllImages = async () => {
    if (!port || !writer) {
      throw new Error('Serial port not connected');
    }

    try {
      setIsClearing(true);
      setUploadStatus('Clearing all images from SPIFFS...');

      const encoder = new TextEncoder();
      const clearCommand = 'delete**images**all';

      await writer.write(encoder.encode(clearCommand + '\n'));
      addLog(`Clear command sent: ${clearCommand}`, 'info');

      // Wait for response
      try {
        await waitForResponse(5000);
        addLog('All images cleared successfully', 'success');
        setUploadStatus('All images cleared successfully');
      } catch (error) {
        addLog('Clear command sent (no response received)', 'warning');
        setUploadStatus('Clear command sent');
      }

      // Wait a bit for the operation to complete
      await new Promise(resolve => setTimeout(resolve, 1000));

    } catch (error) {
      addLog(`Clear failed: ${error.message}`, 'error');
      setUploadStatus(`Clear failed: ${error.message}`);
      throw error;
    } finally {
      setIsClearing(false);
    }
  };

  // Fetch images from API
  const fetchImages = async () => {
    setIsLoadingImages(true);
    setApiError('');
    try {
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'accept': '*/*'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Extract images from the API response
      let imageUrls = [];
      if (data && data.images && Array.isArray(data.images)) {
        imageUrls = data.images;
      } else if (data && Array.isArray(data)) {
        imageUrls = data;
      } else if (data && typeof data === 'object') {
        // Search for image URLs in the response
        const findImageUrls = (obj) => {
          const urls = [];
          for (const key in obj) {
            if (typeof obj[key] === 'string' && (obj[key].includes('.jpg') || obj[key].includes('.jpeg') || obj[key].includes('.png'))) {
              urls.push(obj[key]);
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
              urls.push(...findImageUrls(obj[key]));
            }
          }
          return urls;
        };
        imageUrls = findImageUrls(data);
      }

      setImages(imageUrls);
      setCurrentImageIndex(0);
      addLog(`Fetched ${imageUrls.length} images from API`, 'success');
    } catch (error) {
      setApiError(`Failed to fetch images: ${error.message}`);
      addLog(`API Error: ${error.message}`, 'error');
    } finally {
      setIsLoadingImages(false);
    }
  };

  // Convert image URL to JPEG blob
  const convertImageToJpeg = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new window.Image(); // Use window.Image to ensure we get the native constructor
        
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob((jpegBlob) => {
            if (jpegBlob) {
              resolve(jpegBlob);
            } else {
              reject(new Error('Failed to convert image to JPEG'));
            }
          }, 'image/jpeg', 0.9);
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageUrl;
      });
    } catch (error) {
      throw new Error(`Failed to convert image: ${error.message}`);
    }
  };

  // Serial connection functions
  const connectSerial = async () => {
    try {
      const selectedPort = await navigator.serial.requestPort();
      await selectedPort.open({ baudRate: parseInt(baudRate, 10) });
      const portWriter = selectedPort.writable.getWriter();
      
      setPort(selectedPort);
      setWriter(portWriter);
      setIsConnected(true);
      
      addLog(`Serial port connected at ${baudRate} baud`, 'success');
      
      // Start reading loop
      readLoop(selectedPort);
      
      // Auto-clear images if not done yet and images are loaded
      if (!hasInitialCleared && images.length > 0) {
        setTimeout(async () => {
          try {
            await clearAllImages();
            setHasInitialCleared(true);
          } catch (error) {
            addLog(`Auto-clear failed: ${error.message}`, 'error');
          }
        }, 1000);
      }
    } catch (error) {
      addLog(`Connection error: ${error.message}`, 'error');
    }
  };

  const disconnectSerial = async () => {
    try {
      if (writer) {
        writer.releaseLock();
        setWriter(null);
      }
      if (reader) {
        reader.releaseLock();
        setReader(null);
      }
      if (port) {
        await port.close();
        setPort(null);
      }
      
      setIsConnected(false);
      setHasInitialCleared(false); // Reset for next connection
      addLog('Serial port disconnected', 'success');
    } catch (error) {
      addLog(`Disconnection error: ${error.message}`, 'error');
    }
  };

  const readLoop = async (serialPort) => {
    try {
      const portReader = serialPort.readable.getReader();
      setReader(portReader);
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await portReader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        responseBuffer.current += chunk;

        if (responseCallback.current) {
          responseCallback.current();
          responseCallback.current = null;
        }

        let lines = responseBuffer.current.split('\n');
        responseBuffer.current = lines.pop();

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine) {
            addLog(`Received: ${trimmedLine}`, 'info');
          }
        }
      }
    } catch (error) {
      addLog(`Read loop error: ${error.message}`, 'error');
    }
  };

  const waitForResponse = (timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        responseCallback.current = null;
        reject(new Error('Timeout waiting for response'));
      }, timeout);

      responseCallback.current = () => {
        clearTimeout(timeoutId);
        resolve();
      };
    });
  };

  const uploadImage = async (imageUrl, fileName) => {
    if (!port || !writer) {
      throw new Error('Serial port not connected');
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('Converting image to JPEG...');

      const jpegBlob = await convertImageToJpeg(imageUrl);
      const arrayBuffer = await jpegBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      const encoder = new TextEncoder();
      const startCommand = `sending**${fileName}**${uint8Array.length}**${chunkSize}`;
      
      await writer.write(encoder.encode(startCommand + '\n'));
      addLog(`Upload start command: ${startCommand}`, 'info');
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const totalChunks = Math.ceil(uint8Array.length / chunkSize);
      addLog(`Starting upload: ${totalChunks} chunks of ${chunkSize} bytes each`, 'info');

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, uint8Array.length);
        const chunk = uint8Array.slice(start, end);

        await writer.write(chunk);
        addLog(`Sent chunk ${i + 1}/${totalChunks} (${chunk.length} bytes)`, 'info');

        try {
          await waitForResponse(1000);
        } catch (error) {
          addLog(`No response for chunk ${i + 1}, continuing...`, 'warning');
        }

        const progress = Math.round(((i + 1) / totalChunks) * 100);
        setUploadProgress(progress);
        setUploadStatus(`Uploading: ${progress}% (${i + 1}/${totalChunks})`);

        if (bufferTimer > 0) {
          await new Promise(resolve => setTimeout(resolve, bufferTimer));
        }
      }

      setUploadStatus('Upload complete!');
      addLog(`Upload completed successfully: ${fileName}`, 'success');
      
    } catch (error) {
      addLog(`Upload failed: ${error.message}`, 'error');
      setUploadStatus(`Upload failed: ${error.message}`);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const uploadCurrentImage = async () => {
    if (images.length === 0 || currentImageIndex >= images.length) return;
    
    const imageUrl = images[currentImageIndex];
    const fileName = `${currentImageIndex + 1}.jpeg`;
    
    try {
      await uploadImage(imageUrl, fileName);
      
      if (isAutoUpload && currentImageIndex < images.length - 1) {
        setTimeout(() => {
          setCurrentImageIndex(prev => prev + 1);
        }, 1000);
      }
    } catch (error) {
      setIsAutoUpload(false);
    }
  };

  const startAutoUpload = async () => {
    if (!hasInitialCleared && isConnected) {
      try {
        await clearAllImages();
        setHasInitialCleared(true);
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        addLog(`Failed to clear images before auto upload: ${error.message}`, 'error');
        return;
      }
    }
    
    setIsAutoUpload(true);
    setCurrentImageIndex(0);
  };

  const stopAutoUpload = () => {
    setIsAutoUpload(false);
  };

  const manualClearImages = async () => {
    try {
      await clearAllImages();
      setHasInitialCleared(true);
    } catch (error) {
      addLog(`Manual clear failed: ${error.message}`, 'error');
    }
  };

  // Auto upload when image index changes and auto upload is enabled
  useEffect(() => {
    if (isAutoUpload && !isUploading && images.length > 0 && currentImageIndex < images.length) {
      uploadCurrentImage();
    }
  }, [currentImageIndex, isAutoUpload]);

  // Auto scroll log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [log]);

  // Load images on component mount
  useEffect(() => {
    fetchImages();
  }, []);

  // Auto-clear when both connected and images are loaded
  useEffect(() => {
    if (isConnected && !hasInitialCleared && images.length > 0 && !isClearing) {
      const timer = setTimeout(async () => {
        try {
          await clearAllImages();
          setHasInitialCleared(true);
        } catch (error) {
          addLog(`Auto-clear failed: ${error.message}`, 'error');
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isConnected, images.length, hasInitialCleared, isClearing]);

  return (
    <div className="flex gap-5 p-5 bg-gray-50 min-h-screen">
      <div className="flex-1 max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">SPIFFS JPEG Upload with Auto Clear</h1>

        {/* Connection Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            {isConnected ? <Wifi className="text-green-500" /> : <WifiOff className="text-red-500" />}
            Serial Connection
          </h2>
          
          <div className={`p-4 rounded-lg text-center font-semibold mb-4 ${
            isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {isConnected ? 'Connected' : 'Disconnected'}
            {isConnected && !hasInitialCleared && (
              <div className="text-sm font-normal mt-1">
                Auto-clear will run when images are loaded
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="font-medium">Baud Rate:</label>
              <select 
                value={baudRate} 
                onChange={(e) => setBaudRate(e.target.value)}
                disabled={isConnected}
                className="px-3 py-2 border rounded-md"
              >
                {baudRateOptions.map(rate => (
                  <option key={rate} value={rate}>{rate} baud</option>
                ))}
              </select>
            </div>
            
            <button
              onClick={connectSerial}
              disabled={isConnected}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:bg-gray-400"
            >
              Connect
            </button>
            
            <button
              onClick={disconnectSerial}
              disabled={!isConnected}
              className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 disabled:bg-gray-400"
            >
              Disconnect
            </button>

            <button
              onClick={manualClearImages}
              disabled={!isConnected || isClearing || isUploading}
              className="bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 disabled:bg-gray-400 flex items-center gap-2"
            >
              <Trash2 size={16} />
              {isClearing ? 'Clearing...' : 'Clear All Images'}
            </button>
          </div>
        </div>

        {/* API Images Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <ImageIcon />
            API Images
          </h2>
          
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={fetchImages}
              disabled={isLoadingImages}
              className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 disabled:bg-gray-400 flex items-center gap-2"
            >
              <Download size={16} />
              {isLoadingImages ? 'Loading...' : 'Refresh Images'}
            </button>
            
            <span className="text-sm text-gray-600">
              {images.length} images loaded
            </span>

            {isConnected && !hasInitialCleared && (
              <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">
                Images will be auto-cleared when connected
              </span>
            )}
          </div>

          {apiError && (
            <div className="bg-red-100 text-red-800 p-3 rounded-md mb-4">
              {apiError}
            </div>
          )}

          {images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
              {images.map((imageUrl, index) => (
                <div 
                  key={index}
                  className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                    index === currentImageIndex ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img
                    src={imageUrl}
                    alt={`Image ${index + 1}`}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
                    Image {index + 1}
                  </div>
                  {index === currentImageIndex && (
                    <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                      ▶
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Upload />
            Upload Control
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-medium mb-2">Chunk Size (bytes):</label>
              <input
                type="number"
                value={chunkSize}
                onChange={(e) => setChunkSize(Math.max(256, Math.min(4096, parseInt(e.target.value) || 1024)))}
                min="256"
                max="4096"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            
            <div>
              <label className="block font-medium mb-2">Buffer Timer (ms):</label>
              <input
                type="number"
                value={bufferTimer}
                onChange={(e) => setBufferTimer(Math.max(10, Math.min(1000, parseInt(e.target.value) || 50)))}
                min="10"
                max="1000"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={uploadCurrentImage}
              disabled={!isConnected || isUploading || images.length === 0 || isClearing}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-2"
            >
              <Upload size={16} />
              Upload Current Image
            </button>

            <button
              onClick={isAutoUpload ? stopAutoUpload : startAutoUpload}
              disabled={!isConnected || images.length === 0 || isClearing}
              className={`px-4 py-2 rounded-md text-white flex items-center gap-2 ${
                isAutoUpload ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
              } disabled:bg-gray-400`}
            >
              {isAutoUpload ? <Pause size={16} /> : <Play size={16} />}
              {isAutoUpload ? 'Stop Auto Upload' : 'Start Auto Upload'}
            </button>

            <button
              onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
              disabled={currentImageIndex === 0}
              className="bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600 disabled:bg-gray-400"
            >
              ◀ Prev
            </button>

            <button
              onClick={() => setCurrentImageIndex(prev => Math.min(images.length - 1, prev + 1))}
              disabled={currentImageIndex >= images.length - 1}
              className="bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600 disabled:bg-gray-400"
            >
              Next ▶
            </button>
          </div>

          {images.length > 0 && (
            <div className="text-sm text-gray-600 mb-4">
              Current: Image {currentImageIndex + 1} of {images.length}
            </div>
          )}

          {(isUploading || isClearing) && (
            <div className="mb-4">
              <div className="bg-gray-200 rounded-full h-4 mb-2">
                <div
                  className={`h-4 rounded-full transition-all duration-300 ${
                    isClearing ? 'bg-orange-500' : 'bg-green-500'
                  }`}
                  style={{ width: isClearing ? '100%' : `${uploadProgress}%` }}
                ></div>
              </div>
              <div className="text-sm font-medium text-gray-700">{uploadStatus}</div>
            </div>
          )}
        </div>
      </div>

      {/* Log Section */}
      <div className="w-80">
        <h2 className="text-xl font-semibold mb-4">Log Output</h2>
        <div
          ref={logRef}
          className="bg-gray-900 text-green-400 p-4 rounded-md h-96 overflow-y-auto text-sm font-mono whitespace-pre-wrap"
        >
          {log || 'Log output will appear here...'}
        </div>
        <button
          onClick={clearLog}
          className="mt-2 bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 w-full"
        >
          Clear Log
        </button>
      </div>
    </div>
  );
};

export default SPIFFSUploader;